import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { toPng } from "html-to-image";
import { 
  Calculator, BarChart3, GitBranch, Clock,
  FileText, Ship, Shield, Building2, Stamp, Percent,
  Briefcase, Truck, Package, Warehouse, MoreHorizontal,
  Globe, ChevronDown, ChevronUp, DollarSign, TrendingUp,
  AlertTriangle, CheckCircle, Target, RefreshCw, Tag,
  Box, PieChart, Search, X, Ban, Check, Download,
  User, Mail, Phone, TrendingDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations object
const translations = {
  es: {
    // Header
    title: "Calculadora de Costos de Importación",
    // Tabs
    calculator: "Calculadora",
    results: "Resultados",
    scenarios: "Escenarios",
    history: "Historial",
    leads: "Leads",
    workflow: "Workflow:",
    // Customer Info
    customerData: "Datos del Cliente",
    customerInfoDesc: "Información para la cotización",
    customerName: "Nombre del Cliente",
    customerEmail: "Correo Electrónico",
    customerPhone: "Teléfono",
    customerNamePlaceholder: "Ej: Empresa ABC S.A. de C.V.",
    customerEmailPlaceholder: "Ej: contacto@empresa.com",
    customerPhonePlaceholder: "Ej: +52 55 1234 5678",
    // Tariff Section
    tariffSection: "Fracción Arancelaria (TIGIE)",
    tariffDesc: "Busca tu fracción arancelaria para obtener el IGI automáticamente",
    tariffSearchPlaceholder: "Buscar por código o descripción... (ej: 8471.30, teléfono)",
    prohibited: "Prohibida",
    exempt: "Exento",
    importProhibited: "Importación Prohibida",
    exemptIGI: "Exento de IGI",
    formula: "Fórmula",
    unit: "Unidad",
    autoFillHint: "Auto-llenado desde TIGIE",
    // Import Details
    importDetails: "Detalles de Importación",
    invoiceValue: "Valor de Factura Comercial (USD)",
    invoicePlaceholder: "Ej: 50,000",
    maritimeFreight: "Flete Marítimo (USD)",
    freightPlaceholder: "Ej: 2,500",
    exchangeRate: "Tipo de Cambio (USD/MXN)",
    exchangeRateHint: "El tipo de cambio lo asignará el SAT el día de la liberación aduanal.",
    units: "Unidades",
    unitsPlaceholder: "Ej: 1000",
    productCategory: "Categoría de Producto",
    // Tax Rates
    taxRates: "Tasas de Impuestos",
    igiRate: "IGI - Impuesto General de Importación (%)",
    igeRate: "IGE - Impuesto General de Exportación (%)",
    dtaRate: "DTA - Derecho de Trámite Aduanero (%)",
    ivaRate: "IVA (%)",
    // Additional Costs
    additionalCosts: "Costos Adicionales (MXN)",
    importService: "Servicio Integral de Importación 24/7",
    portHandling: "Maniobras Portuarias Estimadas",
    electronicManifest: "Manifestación de Valor Electrónica",
    possibleStorage: "Posible Almacenaje y Maniobras",
    brokerFee: "Honorarios de Agente Aduanal",
    inlandFreight: "Flete Terrestre Directo",
    containerWashing: "Lavado de Contenedor",
    cargoInsurance: "Seguro de Carga Terrestre",
    shippingCharges: "Cargos Locales de Naviera",
    dmaFee: "Servicios DMA International",
    // Calculate Button
    calculating: "Calculando...",
    calculateButton: "Calcular Costo de Importación",
    // Results
    totalImportCost: "Costo Total de Importación (MXN)",
    perUnit: "por unidad",
    invoiceValueLabel: "Valor Factura",
    totalTaxes: "Total Impuestos",
    logistics: "Logística",
    importCosts: "Costos de Importación",
    downloadQuote: "Descargar Cotización",
    saveCalc: "Guardar Cálculo",
    edit: "Editar",
    // NOM
    noNOMRequired: "Sin Certificación NOM Requerida",
    optional: "Opcional",
    noNOMDesc: "Esta categoría de producto no requiere certificación NOM obligatoria para el despacho aduanal.",
    nomRequired: "Certificación NOM Requerida",
    mandatory: "Obligatoria",
    nomDesc: "Esta categoría de producto requiere certificación NOM antes del despacho aduanal. Asegúrese de cumplir con los requisitos para evitar retrasos y sanciones.",
    // History
    noSavedCalcs: "Sin Cálculos Guardados",
    historyDesc: "Tu historial de cálculos aparecerá aquí una vez que guardes cálculos.",
    calcHistory: "Historial de Cálculos",
    load: "Cargar",
    delete: "Eliminar",
    // Scenarios
    scenarioComparison: "Comparación de Escenarios",
    scenarioDesc: "Compara diferentes escenarios de importación ajustando variables.",
    scenarioSoon: "¡Función de comparación de escenarios próximamente!",
    scenarioSoonDesc: "Podrás comparar diferentes tipos de cambio, tasas de impuestos y cantidades.",
    // Leads
    leadsDashboard: "Dashboard de Leads",
    leadsSubtitle: "Clientes que han utilizado la calculadora",
    filterByDate: "Filtrar por fecha:",
    all: "Todos",
    today: "Hoy",
    lastWeek: "Última semana",
    lastMonth: "Último mes",
    searchPlaceholder: "Buscar por nombre, email o teléfono...",
    refresh: "Actualizar",
    exportCSV: "Exportar CSV",
    totalLeads: "Total Leads",
    filtered: "Filtrados",
    noLeads: "No hay leads",
    leadsWillAppear: "Los leads aparecerán aquí cuando los clientes completen sus datos en la calculadora.",
    name: "Nombre",
    email: "Email",
    phone: "Teléfono",
    date: "Fecha",
    actions: "Acciones",
    // Footer
    importantNotice: "AVISO IMPORTANTE:",
    footerDisclaimer: "Toda la información presentada en esta calculadora es de carácter REFERENCIAL y puede cambiar en cualquier momento sin previo aviso. Los tipos de cambio, aranceles, impuestos y costos logísticos están sujetos a variaciones según las condiciones del mercado y regulaciones vigentes. DMA International no se hace responsable por cambios en los valores finales ni por decisiones tomadas con base en esta información.",
    allRightsReserved: "Todos los derechos reservados",
    // Quote Card
    quoteTitle: "Cotización de Costos de Importación",
    quoteDate: "Fecha",
    client: "Cliente",
    fraction: "Fracción",
    nationalizationCost: "COSTO TOTAL DE NACIONALIZACIÓN",
    merchandiseValue: "Valor de mercancía (ref.):",
    concept: "CONCEPTO",
    amountMXN: "IMPORTE MXN",
    customsTaxes: "IMPUESTOS ADUANEROS",
    igiLabel: "Impuesto General de Importación (IGI)",
    dtaLabel: "Derecho de Trámite Aduanero (DTA)",
    ivaLabel: "IVA (16%)",
    subtotalTaxes: "Subtotal Impuestos",
    dispatchLogistics: "GASTOS DE DESPACHO Y LOGÍSTICA",
    subtotalExpenses: "Subtotal Gastos",
    totalNationalization: "TOTAL COSTO DE NACIONALIZACIÓN",
    appliedExchangeRate: "Tipo de Cambio Aplicado:",
    referentialQuote: "COTIZACIÓN REFERENCIAL",
    quoteDisclaimer: "Los valores mostrados son estimaciones y pueden variar. El tipo de cambio definitivo será asignado por el SAT al momento de la liberación aduanal. Impuestos, aranceles y costos logísticos están sujetos a cambios sin previo aviso. DMA International no se hace responsable por variaciones en los costos finales.",
    dmaSlogan: "DMA International - Soluciones en Comercio Exterior",
    // Messages
    saveCalcPrompt: "Ingresa un nombre para este cálculo:",
    calcSaved: "¡Cálculo guardado!",
    confirmDelete: "¿Estás seguro de que deseas eliminar este cálculo?",
    calcError: "Error calculating costs. Please check your inputs.",
    quoteError: "Error al generar la imagen. Por favor intente de nuevo."
  },
  en: {
    // Header
    title: "Import Cost Calculator",
    // Tabs
    calculator: "Calculator",
    results: "Results",
    scenarios: "Scenarios",
    history: "History",
    leads: "Leads",
    workflow: "Workflow:",
    // Customer Info
    customerData: "Customer Information",
    customerInfoDesc: "Information for the quote",
    customerName: "Customer Name",
    customerEmail: "Email Address",
    customerPhone: "Phone",
    customerNamePlaceholder: "E.g.: ABC Company Inc.",
    customerEmailPlaceholder: "E.g.: contact@company.com",
    customerPhonePlaceholder: "E.g.: +1 555 123 4567",
    // Tariff Section
    tariffSection: "Tariff Code (TIGIE)",
    tariffDesc: "Search your tariff code to get the IGI automatically",
    tariffSearchPlaceholder: "Search by code or description... (e.g.: 8471.30, phone)",
    prohibited: "Prohibited",
    exempt: "Exempt",
    importProhibited: "Import Prohibited",
    exemptIGI: "Exempt from IGI",
    formula: "Formula",
    unit: "Unit",
    autoFillHint: "Auto-filled from TIGIE",
    // Import Details
    importDetails: "Import Details",
    invoiceValue: "Commercial Invoice Value (USD)",
    invoicePlaceholder: "E.g.: 50,000",
    maritimeFreight: "Maritime Freight (USD)",
    freightPlaceholder: "E.g.: 2,500",
    exchangeRate: "Exchange Rate (USD/MXN)",
    exchangeRateHint: "The exchange rate will be assigned by SAT on the day of customs clearance.",
    units: "Units",
    unitsPlaceholder: "E.g.: 1000",
    productCategory: "Product Category",
    // Tax Rates
    taxRates: "Tax Rates",
    igiRate: "IGI - General Import Tax (%)",
    igeRate: "IGE - General Export Tax (%)",
    dtaRate: "DTA - Customs Processing Fee (%)",
    ivaRate: "VAT (%)",
    // Additional Costs
    additionalCosts: "Additional Costs (MXN)",
    importService: "Comprehensive Import Service 24/7",
    portHandling: "Estimated Port Handling",
    electronicManifest: "Electronic Value Manifest",
    possibleStorage: "Possible Storage and Handling",
    brokerFee: "Customs Broker Fee",
    inlandFreight: "Direct Inland Transportation",
    containerWashing: "Container Washing",
    cargoInsurance: "Inland Cargo Insurance",
    shippingCharges: "Shipping Line Local Charges",
    dmaFee: "DMA International Services",
    // Calculate Button
    calculating: "Calculating...",
    calculateButton: "Calculate Import Cost",
    // Results
    totalImportCost: "Total Import Cost (MXN)",
    perUnit: "per unit",
    invoiceValueLabel: "Invoice Value",
    totalTaxes: "Total Taxes",
    logistics: "Logistics",
    importCosts: "Import Costs",
    downloadQuote: "Download Quote",
    saveCalc: "Save Calculation",
    edit: "Edit",
    // NOM
    noNOMRequired: "No NOM Certification Required",
    optional: "Optional",
    noNOMDesc: "This product category does not require mandatory NOM certification for customs clearance.",
    nomRequired: "NOM Certification Required",
    mandatory: "Mandatory",
    nomDesc: "This product category requires NOM certification before customs clearance. Make sure to comply with the requirements to avoid delays and penalties.",
    // History
    noSavedCalcs: "No Saved Calculations",
    historyDesc: "Your calculation history will appear here once you save calculations.",
    calcHistory: "Calculation History",
    load: "Load",
    delete: "Delete",
    // Scenarios
    scenarioComparison: "Scenario Comparison",
    scenarioDesc: "Compare different import scenarios by adjusting variables.",
    scenarioSoon: "Scenario comparison feature coming soon!",
    scenarioSoonDesc: "You will be able to compare different exchange rates, tax rates, and quantities.",
    // Leads
    leadsDashboard: "Leads Dashboard",
    leadsSubtitle: "Customers who have used the calculator",
    filterByDate: "Filter by date:",
    all: "All",
    today: "Today",
    lastWeek: "Last week",
    lastMonth: "Last month",
    searchPlaceholder: "Search by name, email or phone...",
    refresh: "Refresh",
    exportCSV: "Export CSV",
    totalLeads: "Total Leads",
    filtered: "Filtered",
    noLeads: "No leads",
    leadsWillAppear: "Leads will appear here when customers complete their information in the calculator.",
    name: "Name",
    email: "Email",
    phone: "Phone",
    date: "Date",
    actions: "Actions",
    // Footer
    importantNotice: "IMPORTANT NOTICE:",
    footerDisclaimer: "All information presented in this calculator is REFERENTIAL and may change at any time without prior notice. Exchange rates, tariffs, taxes, and logistics costs are subject to variations according to market conditions and current regulations. DMA International is not responsible for changes in final values or decisions made based on this information.",
    allRightsReserved: "All rights reserved",
    // Quote Card
    quoteTitle: "Import Cost Quote",
    quoteDate: "Date",
    client: "Client",
    fraction: "Tariff Code",
    nationalizationCost: "TOTAL NATIONALIZATION COST",
    merchandiseValue: "Merchandise value (ref.):",
    concept: "CONCEPT",
    amountMXN: "AMOUNT MXN",
    customsTaxes: "CUSTOMS TAXES",
    igiLabel: "General Import Tax (IGI)",
    dtaLabel: "Customs Processing Fee (DTA)",
    ivaLabel: "VAT (16%)",
    subtotalTaxes: "Subtotal Taxes",
    dispatchLogistics: "DISPATCH AND LOGISTICS EXPENSES",
    subtotalExpenses: "Subtotal Expenses",
    totalNationalization: "TOTAL NATIONALIZATION COST",
    appliedExchangeRate: "Applied Exchange Rate:",
    referentialQuote: "REFERENTIAL QUOTE",
    quoteDisclaimer: "Values shown are estimates and may vary. The final exchange rate will be assigned by SAT at the time of customs clearance. Taxes, tariffs, and logistics costs are subject to change without prior notice. DMA International is not responsible for variations in final costs.",
    dmaSlogan: "DMA International - Foreign Trade Solutions",
    // Messages
    saveCalcPrompt: "Enter a name for this calculation:",
    calcSaved: "Calculation saved!",
    confirmDelete: "Are you sure you want to delete this calculation?",
    calcError: "Error calculating costs. Please check your inputs.",
    quoteError: "Error generating image. Please try again."
  }
};

// Format currency
const formatCurrency = (amount, currency = "MXN") => {
  return new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: currency === "MXN" ? "MXN" : "USD",
    minimumFractionDigits: 2,
  }).format(amount).replace("MX$", "MX$");
};

// Icon mapping
const iconMap = {
  document: FileText,
  ship: Ship,
  shield: Shield,
  bank: Building2,
  stamp: Stamp,
  percent: Percent,
  briefcase: Briefcase,
  truck: Truck,
  package: Package,
  warehouse: Warehouse,
  ellipsis: MoreHorizontal,
  globe: Globe,
};

// Compact Banxico Reference for Import Details
const BanxicoRateInline = ({ banxicoRate, onUseRate }) => {
  if (!banxicoRate) return null;
  
  return (
    <div className="banxico-inline" data-testid="banxico-inline">
      <div className="banxico-inline-info">
        <span className="banxico-inline-label">Banxico FIX:</span>
        <span className="banxico-inline-rate">{banxicoRate.rate?.toFixed(4) || "---"}</span>
        <span className="banxico-inline-date">({banxicoRate.date})</span>
      </div>
      <button 
        className="banxico-inline-btn"
        onClick={onUseRate}
        disabled={!banxicoRate?.rate}
        data-testid="use-banxico-inline"
      >
        Usar
      </button>
    </div>
  );
};

// Leads Dashboard Component
const LeadsDashboard = ({ leads, onRefresh, loading, language }) => {
  const t = translations[language];
  const [dateFilter, setDateFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const filteredLeads = leads.filter(lead => {
    // Date filter
    if (dateFilter !== "all") {
      const leadDate = new Date(lead.created_at);
      const now = new Date();
      if (dateFilter === "today") {
        if (leadDate.toDateString() !== now.toDateString()) return false;
      } else if (dateFilter === "week") {
        const weekAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
        if (leadDate < weekAgo) return false;
      } else if (dateFilter === "month") {
        const monthAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        if (leadDate < monthAgo) return false;
      }
    }
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        lead.name?.toLowerCase().includes(term) ||
        lead.email?.toLowerCase().includes(term) ||
        lead.phone?.toLowerCase().includes(term)
      );
    }
    
    return true;
  });

  const exportToExcel = () => {
    const headers = [t.name, t.email, t.phone, t.date];
    const rows = filteredLeads.map(lead => [
      lead.name || "",
      lead.email || "",
      lead.phone || "",
      new Date(lead.created_at).toLocaleString(language === 'es' ? "es-MX" : "en-US")
    ]);
    
    const csvContent = [
      headers.join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");
    
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `leads_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <div className="leads-dashboard" data-testid="leads-dashboard">
      <div className="leads-header">
        <h2>{t.leadsDashboard}</h2>
        <p className="leads-subtitle">{t.leadsSubtitle}</p>
      </div>

      <div className="leads-controls">
        <div className="leads-filters">
          <div className="filter-group">
            <label>{t.filterByDate}</label>
            <select 
              value={dateFilter} 
              onChange={(e) => setDateFilter(e.target.value)}
              data-testid="date-filter"
            >
              <option value="all">{t.all}</option>
              <option value="today">{t.today}</option>
              <option value="week">{t.lastWeek}</option>
              <option value="month">{t.lastMonth}</option>
            </select>
          </div>
          <div className="filter-group search-group">
            <Search size={16} />
            <input
              type="text"
              placeholder={t.searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="leads-search"
            />
          </div>
        </div>
        <div className="leads-actions">
          <button className="refresh-btn" onClick={onRefresh} disabled={loading}>
            <RefreshCw size={16} className={loading ? "spin" : ""} />
            {t.refresh}
          </button>
          <button className="export-btn" onClick={exportToExcel} disabled={filteredLeads.length === 0}>
            <FileText size={16} />
            {t.exportCSV}
          </button>
        </div>
      </div>

      <div className="leads-stats">
        <div className="stat-card">
          <span className="stat-value">{leads.length}</span>
          <span className="stat-label">{t.totalLeads}</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{filteredLeads.length}</span>
          <span className="stat-label">{t.filtered}</span>
        </div>
      </div>

      {filteredLeads.length === 0 ? (
        <div className="leads-empty">
          <User size={48} />
          <h3>{t.noLeads}</h3>
          <p>{t.leadsWillAppear}</p>
        </div>
      ) : (
        <div className="leads-table-container">
          <table className="leads-table" data-testid="leads-table">
            <thead>
              <tr>
                <th>{t.name}</th>
                <th>{t.email}</th>
                <th>{t.phone}</th>
                <th>{t.date}</th>
                <th>{t.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead) => (
                <tr key={lead.id}>
                  <td className="lead-name">{lead.name || "-"}</td>
                  <td>
                    {lead.email ? (
                      <a href={`mailto:${lead.email}`} className="lead-email">
                        {lead.email}
                      </a>
                    ) : "-"}
                  </td>
                  <td>
                    {lead.phone ? (
                      <a href={`tel:${lead.phone}`} className="lead-phone">
                        {lead.phone}
                      </a>
                    ) : "-"}
                  </td>
                  <td className="lead-date">
                    {new Date(lead.created_at).toLocaleDateString(language === 'es' ? "es-MX" : "en-US", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </td>
                  {/* Removed WhatsApp CTA for internal corporate use */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Customer Info Form Component
const CustomerInfoForm = ({ customerInfo, setCustomerInfo, language }) => {
  const t = translations[language];
  const handleChange = (field, value) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="customer-info-section" data-testid="customer-info-section">
      <h3><User size={20} /> {t.customerData}</h3>
      <p className="section-description">{t.customerInfoDesc}</p>
      <div className="customer-form-grid">
        <div className="form-group">
          <label><User size={14} /> {t.customerName}</label>
          <input
            type="text"
            data-testid="input-customer-name"
            value={customerInfo.name}
            onChange={(e) => handleChange("name", e.target.value)}
            placeholder={t.customerNamePlaceholder}
          />
        </div>
        <div className="form-group">
          <label><Mail size={14} /> {t.customerEmail}</label>
          <input
            type="email"
            data-testid="input-customer-email"
            value={customerInfo.email}
            onChange={(e) => handleChange("email", e.target.value)}
            placeholder={t.customerEmailPlaceholder}
          />
        </div>
        <div className="form-group">
          <label><Phone size={14} /> {t.customerPhone}</label>
          <input
            type="tel"
            data-testid="input-customer-phone"
            value={customerInfo.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            placeholder={t.customerPhonePlaceholder}
          />
        </div>
      </div>
    </div>
  );
};

// Tab Navigation Component
const TabNavigation = ({ activeTab, setActiveTab, calculationComplete, language }) => {
  const t = translations[language];
  const tabs = [
    { id: "calculator", label: t.calculator, icon: Calculator, active: true },
    { id: "results", label: t.results, icon: BarChart3, active: calculationComplete },
    { id: "scenarios", label: t.scenarios, icon: GitBranch, active: calculationComplete },
    { id: "history", label: t.history, icon: Clock, active: true },
    { id: "leads", label: t.leads, icon: User, active: true },
  ];

  return (
    <div className="tab-navigation" data-testid="tab-navigation">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          data-testid={`tab-${tab.id}`}
          className={`tab-button ${activeTab === tab.id ? "active" : ""} ${!tab.active ? "disabled" : ""}`}
          onClick={() => tab.active && setActiveTab(tab.id)}
          disabled={!tab.active}
        >
          <tab.icon size={18} />
          <span>{tab.label}</span>
          {tab.active && activeTab === tab.id && <span className="tab-indicator" />}
        </button>
      ))}
      <div className="workflow-indicator">
        <span>{t.workflow}</span>
        <span className={`status-dot ${calculationComplete ? "complete" : "pending"}`} />
      </div>
    </div>
  );
};

// Calculator Form Component
const CalculatorForm = ({ formData, setFormData, onCalculate, loading, categories, banxicoRate, exchangeRate, setExchangeRate, language }) => {
  const t = translations[language];
  const [tariffSearch, setTariffSearch] = useState("");
  const [tariffResults, setTariffResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showTariffDropdown, setShowTariffDropdown] = useState(false);
  const [selectedTariff, setSelectedTariff] = useState(null);
  const [skipNextSearch, setSkipNextSearch] = useState(false);

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value === "" ? "" : parseFloat(value)
      }
    }));
  };

  // Debounced tariff search
  const searchTariffs = useCallback(async (query) => {
    if (query.length < 2) {
      setTariffResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const response = await axios.get(`${API}/tariffs/search`, {
        params: { q: query, limit: 20 }
      });
      setTariffResults(response.data.results);
      setShowTariffDropdown(true);
    } catch (error) {
      console.error("Tariff search error:", error);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => {
    if (skipNextSearch) {
      setSkipNextSearch(false);
      return;
    }
    const timer = setTimeout(() => {
      if (tariffSearch && !selectedTariff) {
        searchTariffs(tariffSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [tariffSearch, searchTariffs, skipNextSearch, selectedTariff]);

  const selectTariff = (tariff) => {
    setSkipNextSearch(true);
    setSelectedTariff(tariff);
    setTariffSearch(tariff.code);
    setShowTariffDropdown(false);
    setTariffResults([]);
    
    // Auto-fill the IGI rate if available
    if (tariff.igi_rate !== null && tariff.igi_rate >= 0) {
      setFormData(prev => ({
        ...prev,
        import_costs: {
          ...prev.import_costs,
          igi_rate: tariff.igi_rate / 100, // Convert percentage to decimal
          fraccion_arancelaria: tariff.code
        }
      }));
    }
  };

  const clearTariff = () => {
    setSelectedTariff(null);
    setTariffSearch("");
    setTariffResults([]);
    setShowTariffDropdown(false);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setTariffSearch(value);
    if (selectedTariff && value !== selectedTariff.code) {
      setSelectedTariff(null);
    }
  };

  return (
    <div className="calculator-form" data-testid="calculator-form">
      {/* Fracción Arancelaria Search */}
      <div className="form-section tariff-section">
        <h3><Search size={20} /> {t.tariffSection}</h3>
        <p className="section-description">{t.tariffDesc}</p>
        
        <div className="tariff-search-container">
          <div className="tariff-input-wrapper">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              data-testid="input-tariff-search"
              value={tariffSearch}
              onChange={handleSearchInputChange}
              onFocus={() => !selectedTariff && tariffResults.length > 0 && setShowTariffDropdown(true)}
              placeholder={t.tariffSearchPlaceholder}
              className="tariff-search-input"
            />
            {searchLoading && <RefreshCw size={18} className="spin loading-icon" />}
            {selectedTariff && (
              <button className="clear-tariff-btn" onClick={clearTariff} data-testid="clear-tariff">
                <X size={16} />
              </button>
            )}
          </div>
          
          {showTariffDropdown && tariffResults.length > 0 && (
            <div className="tariff-dropdown" data-testid="tariff-dropdown">
              {tariffResults.map((tariff, index) => (
                <div
                  key={tariff.code}
                  className={`tariff-option ${tariff.is_prohibited ? 'prohibited' : ''} ${tariff.is_exempt ? 'exempt' : ''}`}
                  onClick={() => selectTariff(tariff)}
                  data-testid={`tariff-option-${index}`}
                >
                  <div className="tariff-code-row">
                    <span className="tariff-code">{tariff.code}</span>
                    <span className={`tariff-igi ${tariff.is_prohibited ? 'prohibited' : tariff.is_exempt ? 'exempt' : ''}`}>
                      {tariff.is_prohibited ? (
                        <><Ban size={14} /> {t.prohibited}</>
                      ) : tariff.is_exempt ? (
                        <><Check size={14} /> {t.exempt}</>
                      ) : tariff.igi_rate !== null ? (
                        `IGI: ${tariff.igi_rate}%`
                      ) : tariff.igi_formula ? (
                        <span className="formula-tag">{t.formula}</span>
                      ) : (
                        'N/A'
                      )}
                    </span>
                  </div>
                  <p className="tariff-description">{tariff.description}</p>
                  <span className="tariff-unit">{t.unit}: {tariff.unit}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {selectedTariff && (
          <div className={`selected-tariff-card ${selectedTariff.is_prohibited ? 'prohibited' : selectedTariff.is_exempt ? 'exempt' : ''}`} data-testid="selected-tariff">
            <div className="tariff-card-header">
              <span className="tariff-card-code">{selectedTariff.code}</span>
              {selectedTariff.is_prohibited ? (
                <span className="tariff-badge prohibited"><Ban size={14} /> {t.importProhibited}</span>
              ) : selectedTariff.is_exempt ? (
                <span className="tariff-badge exempt"><Check size={14} /> {t.exemptIGI}</span>
              ) : selectedTariff.igi_rate !== null ? (
                <span className="tariff-badge rate">IGI: {selectedTariff.igi_rate}%</span>
              ) : selectedTariff.igi_formula ? (
                <span className="tariff-badge formula">{t.formula}: {selectedTariff.igi_formula}</span>
              ) : null}
            </div>
            <p className="tariff-card-description">{selectedTariff.description}</p>
            <div className="tariff-card-meta">
              <span>{t.unit}: {selectedTariff.unit}</span>
              <span>IGE: {selectedTariff.ige}</span>
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3><Globe size={20} /> {t.importDetails}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t.invoiceValue}</label>
            <input
              type="number"
              data-testid="input-invoice-value"
              value={formData.import_costs.commercial_invoice_value === "" ? "" : formData.import_costs.commercial_invoice_value}
              onChange={(e) => handleInputChange("import_costs", "commercial_invoice_value", e.target.value)}
              placeholder={t.invoicePlaceholder}
            />
          </div>
          <div className="form-group">
            <label>{t.maritimeFreight}</label>
            <input
              type="number"
              data-testid="input-freight"
              value={formData.import_costs.international_freight === "" ? "" : formData.import_costs.international_freight}
              onChange={(e) => handleInputChange("import_costs", "international_freight", e.target.value)}
              placeholder={t.freightPlaceholder}
            />
          </div>
          <div className="form-group exchange-rate-group">
            <label>{t.exchangeRate}</label>
            <input
              type="number"
              data-testid="input-exchange-rate"
              value={exchangeRate}
              onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)}
              step="0.0001"
            />
            <p className="sat-hint">
              <AlertTriangle size={12} />
              {t.exchangeRateHint}
            </p>
          </div>
          <div className="form-group">
            <label>{t.units}</label>
            <input
              type="number"
              data-testid="input-units"
              value={formData.import_costs.units === "" ? "" : formData.import_costs.units}
              onChange={(e) => handleInputChange("import_costs", "units", e.target.value)}
              min="1"
              placeholder={t.unitsPlaceholder}
            />
          </div>
          <div className="form-group">
            <label>{t.productCategory}</label>
            <select
              data-testid="select-category"
              value={formData.import_costs.product_category}
              onChange={(e) => handleInputChange("import_costs", "product_category", e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{language === 'es' ? cat.name_es : cat.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3><Percent size={20} /> {t.taxRates}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t.igiRate}</label>
            <input
              type="number"
              data-testid="input-igi-rate"
              value={formData.import_costs.igi_rate === "" ? "" : (formData.import_costs.igi_rate * 100).toFixed(1)}
              onChange={(e) => handleInputChange("import_costs", "igi_rate", e.target.value === "" ? "" : parseFloat(e.target.value) / 100)}
              step="0.1"
              placeholder="Ej: 15"
              className={selectedTariff ? "auto-filled" : ""}
            />
            {selectedTariff && <span className="auto-fill-hint">{t.autoFillHint}</span>}
          </div>
          <div className="form-group">
            <label>{t.igeRate}</label>
            <input
              type="number"
              data-testid="input-ige-rate"
              value={formData.import_costs.ige_rate === "" ? "" : (formData.import_costs.ige_rate * 100).toFixed(1)}
              onChange={(e) => handleInputChange("import_costs", "ige_rate", e.target.value === "" ? "" : parseFloat(e.target.value) / 100)}
              step="0.1"
              placeholder="Ej: 0"
            />
          </div>
          <div className="form-group">
            <label>{t.dtaRate}</label>
            <input
              type="number"
              data-testid="input-dta-rate"
              value={(formData.import_costs.dta_rate * 100).toFixed(2)}
              onChange={(e) => handleInputChange("import_costs", "dta_rate", parseFloat(e.target.value) / 100)}
              step="0.01"
            />
          </div>
          <div className="form-group">
            <label>{t.ivaRate}</label>
            <input
              type="number"
              data-testid="input-iva-rate"
              value={(formData.import_costs.iva_rate * 100).toFixed(0)}
              onChange={(e) => handleInputChange("import_costs", "iva_rate", parseFloat(e.target.value) / 100)}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3><Truck size={20} /> {t.additionalCosts}</h3>
        <div className="form-grid">
          <div className="form-group">
            <label>{t.importService}</label>
            <input
              type="number"
              data-testid="input-import-service"
              value={formData.additional_costs.import_service === "" ? "" : formData.additional_costs.import_service}
              onChange={(e) => handleInputChange("additional_costs", "import_service", e.target.value)}
              placeholder="Ej: 8,500"
            />
          </div>
          <div className="form-group">
            <label>{t.portHandling}</label>
            <input
              type="number"
              data-testid="input-port-handling"
              value={formData.additional_costs.port_handling === "" ? "" : formData.additional_costs.port_handling}
              onChange={(e) => handleInputChange("additional_costs", "port_handling", e.target.value)}
              placeholder="Ej: 11,000"
            />
          </div>
          <div className="form-group">
            <label>{t.electronicManifest}</label>
            <input
              type="number"
              data-testid="input-electronic-manifest"
              value={formData.additional_costs.electronic_manifest === "" ? "" : formData.additional_costs.electronic_manifest}
              onChange={(e) => handleInputChange("additional_costs", "electronic_manifest", e.target.value)}
              placeholder="Ej: 900"
            />
          </div>
          <div className="form-group">
            <label>{t.possibleStorage}</label>
            <input
              type="number"
              data-testid="input-possible-storage"
              value={formData.additional_costs.possible_storage === "" ? "" : formData.additional_costs.possible_storage}
              onChange={(e) => handleInputChange("additional_costs", "possible_storage", e.target.value)}
              placeholder="Ej: 5,500"
            />
          </div>
          <div className="form-group">
            <label>{t.brokerFee}</label>
            <input
              type="number"
              data-testid="input-broker-fee"
              value={formData.additional_costs.customs_broker_fee === "" ? "" : formData.additional_costs.customs_broker_fee}
              onChange={(e) => handleInputChange("additional_costs", "customs_broker_fee", e.target.value)}
              placeholder="Ej: 4,500"
            />
          </div>
          <div className="form-group">
            <label>{t.inlandFreight}</label>
            <input
              type="number"
              data-testid="input-inland-freight"
              value={formData.additional_costs.inland_freight === "" ? "" : formData.additional_costs.inland_freight}
              onChange={(e) => handleInputChange("additional_costs", "inland_freight", e.target.value)}
              placeholder="Ej: 28,000"
            />
          </div>
          <div className="form-group">
            <label>{t.containerWashing}</label>
            <input
              type="number"
              data-testid="input-container-washing"
              value={formData.additional_costs.container_washing === "" ? "" : formData.additional_costs.container_washing}
              onChange={(e) => handleInputChange("additional_costs", "container_washing", e.target.value)}
              placeholder="Ej: 1,300"
            />
          </div>
          <div className="form-group">
            <label>{t.cargoInsurance}</label>
            <input
              type="number"
              data-testid="input-cargo-insurance"
              value={formData.additional_costs.cargo_insurance === "" ? "" : formData.additional_costs.cargo_insurance}
              onChange={(e) => handleInputChange("additional_costs", "cargo_insurance", e.target.value)}
              placeholder="Ej: 3,500"
            />
          </div>
          <div className="form-group">
            <label>{t.shippingCharges}</label>
            <input
              type="number"
              data-testid="input-shipping-charges"
              value={formData.additional_costs.shipping_line_charges === "" ? "" : formData.additional_costs.shipping_line_charges}
              onChange={(e) => handleInputChange("additional_costs", "shipping_line_charges", e.target.value)}
              placeholder="Ej: 8,000"
            />
          </div>
          <div className="form-group">
            <label>{t.dmaFee}</label>
            <input
              type="number"
              data-testid="input-dma-fee"
              value={formData.additional_costs.dma_fee === "" ? "" : formData.additional_costs.dma_fee}
              onChange={(e) => handleInputChange("additional_costs", "dma_fee", e.target.value)}
              placeholder="Ej: 5,000"
            />
          </div>
        </div>
      </div>

      <button
        className="calculate-button"
        data-testid="calculate-btn"
        onClick={onCalculate}
        disabled={loading}
      >
        {loading ? (
          <>
            <RefreshCw size={20} className="spin" />
            {t.calculating}
          </>
        ) : (
          <>
            <Calculator size={20} />
            {t.calculateButton}
          </>
        )}
      </button>
    </div>
  );
};

// Cost Breakdown Component
const CostBreakdown = ({ title, costs, subtotal, expanded, onToggle, icon: Icon }) => {
  const costItems = Object.entries(costs).filter(([key]) => key !== "subtotal");

  return (
    <div className="cost-breakdown" data-testid={`breakdown-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="breakdown-header" onClick={onToggle}>
        <div className="breakdown-title">
          <Icon size={20} className="breakdown-icon" />
          <span>{title}</span>
        </div>
        <div className="breakdown-total">
          <span className="total-amount">{formatCurrency(subtotal)}</span>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>
      {expanded && (
        <div className="breakdown-items">
          {costItems.map(([key, item]) => {
            const IconComponent = iconMap[item.icon] || FileText;
            return (
              <div key={key} className="breakdown-item" data-testid={`cost-item-${key}`}>
                <div className="item-name">
                  <IconComponent size={16} className="item-icon" />
                  <span>{item.name_es || item.name}</span>
                </div>
                <div className="item-values">
                  <span className="item-amount">{formatCurrency(item.amount_mxn)}</span>
                  <span className="item-percentage">{item.percentage}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Results Summary Component
const ResultsSummary = ({ result, language }) => {
  const t = translations[language];
  return (
    <div className="results-summary" data-testid="results-summary">
      <div className="summary-header">
        <div className="total-landed-cost">
          <span className="label">{t.totalImportCost}</span>
          <span className="amount">{formatCurrency(result.total_landed_cost)}</span>
          <span className="per-unit">
            {formatCurrency(result.cost_per_unit)} {t.perUnit} · {result.units} {t.units.toLowerCase()}
          </span>
          <span className="usd-reference">
            Ref. USD ${(result.total_landed_cost / result.exchange_rate).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </span>
        </div>
        <div className="summary-cards">
          <div className="summary-card">
            <FileText size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.invoice_value_mxn)}</span>
            <span className="card-label">{t.invoiceValueLabel}</span>
          </div>
          <div className="summary-card">
            <DollarSign size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.total_taxes)}</span>
            <span className="card-label">{t.totalTaxes}</span>
          </div>
          <div className="summary-card">
            <Truck size={24} className="card-icon" />
            <span className="card-amount">{formatCurrency(result.total_logistics)}</span>
            <span className="card-label">{t.logistics}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Financial Viability Component
const FinancialViability = ({ viability }) => {
  return (
    <div className={`financial-viability ${viability.is_viable ? "viable" : "not-viable"}`} data-testid="financial-viability">
      <div className="viability-header">
        {viability.is_viable ? (
          <CheckCircle size={32} className="viability-icon success" />
        ) : (
          <AlertTriangle size={32} className="viability-icon warning" />
        )}
        <div className="viability-title">
          <h3>{viability.is_viable ? "Financially Viable" : "Review Required"}</h3>
          <span className={`viability-badge ${viability.is_viable ? "viable" : "caution"}`}>
            {viability.is_viable ? "Viable" : "Caution"}
          </span>
        </div>
      </div>
      <p className="viability-description">
        {viability.is_viable 
          ? "This import operation shows strong financial viability with healthy margins and acceptable risk levels."
          : "This import operation may need optimization to improve margins and reduce risk."}
      </p>
      <div className="viability-metrics">
        <div className="metric-card">
          <TrendingUp size={24} />
          <span className="metric-value">{viability.projected_margin}%</span>
          <span className="metric-label">Projected Margin</span>
        </div>
        <div className="metric-card">
          <BarChart3 size={24} />
          <span className="metric-value">{viability.roi_estimation}%</span>
          <span className="metric-label">ROI Estimation</span>
        </div>
        <div className="metric-card">
          <Target size={24} />
          <span className="metric-value">{viability.break_even_units}</span>
          <span className="metric-label">Break-even Units</span>
        </div>
        <div className="metric-card">
          <Clock size={24} />
          <span className="metric-value">{viability.payback_period_months} months</span>
          <span className="metric-label">Payback Period</span>
        </div>
      </div>
      <div className="risk-meter">
        <div className="risk-labels">
          <span>Risk Level</span>
          <span>{viability.risk_level}/100</span>
        </div>
        <div className="risk-bar">
          <div 
            className="risk-fill" 
            style={{ width: `${viability.risk_level}%` }}
            data-testid="risk-level-bar"
          />
        </div>
        <div className="risk-scale">
          <span className="low">Low Risk</span>
          <span className="high">High Risk</span>
        </div>
      </div>
    </div>
  );
};

// NOM Requirements Component
const NOMRequirements = ({ requirements, category, language }) => {
  const t = translations[language];
  
  if (!requirements || requirements.length === 0) {
    return (
      <div className="nom-requirements no-requirements" data-testid="nom-requirements">
        <div className="nom-header">
          <CheckCircle size={32} className="nom-icon success" />
          <div>
            <h3>{t.noNOMRequired}</h3>
            <span className="nom-badge optional">{t.optional}</span>
          </div>
        </div>
        <p>{t.noNOMDesc}</p>
      </div>
    );
  }

  return (
    <div className="nom-requirements" data-testid="nom-requirements">
      <div className="nom-header">
        <AlertTriangle size={32} className="nom-icon warning" />
        <div>
          <h3>{t.nomRequired}</h3>
          <span className="nom-badge mandatory">{t.mandatory}</span>
        </div>
      </div>
      <p>{t.nomDesc}</p>
      <div className="nom-codes">
        {requirements.map((nom, index) => (
          <div key={index} className="nom-code" data-testid={`nom-code-${nom.code}`}>
            <FileText size={16} />
            <span>{nom.code}</span>
          </div>
        ))}
      </div>
      
      {/* NOM CTA removed for internal corporate use */}
    </div>
  );
};

// Suggested Pricing Component
const SuggestedPricing = ({ pricing, onRecalculate, currentMargin }) => {
  const [selectedMargin, setSelectedMargin] = useState(currentMargin);

  return (
    <div className="suggested-pricing" data-testid="suggested-pricing">
      <div className="pricing-header">
        <Tag size={32} className="pricing-icon" />
        <h3>Suggested Sales Price</h3>
      </div>
      <div className="price-display">
        <span className="price-label">Recommended Selling Price</span>
        <span className="price-amount">{formatCurrency(pricing.recommended_selling_price)}</span>
        <span className="price-per-unit">per unit</span>
      </div>
      <div className="margin-selection">
        <div className="margin-label">
          <span>Target Margin</span>
          <span className="current-margin">{pricing.target_margin}%</span>
        </div>
        <div className="margin-buttons">
          {[0.15, 0.20, 0.25, 0.30, 0.35, 0.40].map(margin => (
            <button
              key={margin}
              className={`margin-btn ${selectedMargin === margin ? "active" : ""}`}
              onClick={() => {
                setSelectedMargin(margin);
                onRecalculate(margin);
              }}
              data-testid={`pricing-margin-${margin * 100}`}
            >
              {(margin * 100).toFixed(0)}%
            </button>
          ))}
        </div>
      </div>
      <div className="pricing-breakdown">
        <div className="pricing-row">
          <Box size={18} />
          <span>Total Landed Cost</span>
          <span className="value">{formatCurrency(pricing.total_landed_cost)}</span>
        </div>
        <div className="pricing-row profit">
          <TrendingUp size={18} />
          <span>Gross Profit</span>
          <span className="value">{formatCurrency(pricing.gross_profit)}</span>
        </div>
        <div className="pricing-row">
          <BarChart3 size={18} />
          <span>ROI</span>
          <span className="value">{pricing.roi}%</span>
        </div>
      </div>
      <button className="recalculate-btn" onClick={() => onRecalculate(selectedMargin)} data-testid="recalculate-btn">
        <RefreshCw size={18} />
        Recalculate
      </button>
    </div>
  );
};

// History Component
const HistoryTab = ({ history, onLoad, onDelete, language }) => {
  const t = translations[language];
  
  if (!history || history.length === 0) {
    return (
      <div className="history-empty" data-testid="history-empty">
        <Clock size={48} />
        <h3>{t.noSavedCalcs}</h3>
        <p>{t.historyDesc}</p>
      </div>
    );
  }

  return (
    <div className="history-list" data-testid="history-list">
      {history.map((item) => (
        <div key={item.id} className="history-item" data-testid={`history-item-${item.id}`}>
          <div className="history-info">
            <h4>{item.name}</h4>
            <span className="history-date">
              {new Date(item.created_at).toLocaleDateString(language === 'es' ? "es-MX" : "en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit"
              })}
            </span>
            <span className="history-total">{formatCurrency(item.result.total_landed_cost)}</span>
          </div>
          <div className="history-actions">
            <button onClick={() => onLoad(item)} data-testid={`load-${item.id}`}>{t.load}</button>
            <button onClick={() => onDelete(item.id)} className="delete" data-testid={`delete-${item.id}`}>{t.delete}</button>
          </div>
        </div>
      ))}
    </div>
  );
};

// Reference Tab Component
const ReferenceTab = () => {
  return (
    <div className="reference-tab" data-testid="reference-tab">
      <h2>Import Cost Reference Guide</h2>
      
      <div className="reference-section">
        <h3><Globe size={20} /> Mexican Import Taxes & Duties</h3>
        <div className="reference-table">
          <div className="ref-row header">
            <span>Tax/Duty</span>
            <span>Rate</span>
            <span>Description</span>
          </div>
          <div className="ref-row">
            <span>IVA (VAT)</span>
            <span>16%</span>
            <span>Applied to customs value + duties</span>
          </div>
          <div className="ref-row">
            <span>DTA</span>
            <span>0.8%</span>
            <span>Customs processing fee (min $293 MXN)</span>
          </div>
          <div className="ref-row">
            <span>Arancel</span>
            <span>5-35%</span>
            <span>Varies by product HS code</span>
          </div>
        </div>
      </div>

      <div className="reference-section">
        <h3><FileText size={20} /> Common NOM Certifications</h3>
        <div className="reference-list">
          <div className="ref-item">
            <strong>NOM-001-SEDE</strong> - Electrical installations safety
          </div>
          <div className="ref-item">
            <strong>NOM-016-ENER</strong> - Energy efficiency for appliances
          </div>
          <div className="ref-item">
            <strong>NOM-051-SCFI</strong> - Food labeling requirements
          </div>
          <div className="ref-item">
            <strong>NOM-004-SCFI</strong> - Textile labeling requirements
          </div>
        </div>
      </div>

      <div className="reference-section">
        <h3><Calculator size={20} /> Cost Calculation Formula</h3>
        <div className="formula-box">
          <code>
            CIF Value = Invoice + Freight + Insurance<br/>
            Customs Value = CIF × Exchange Rate<br/>
            Total Duties = Customs Value × (Duty Rate + DTA Rate)<br/>
            IVA = (Customs Value + Duties) × 16%<br/>
            Landed Cost = Customs Value + Duties + IVA + Logistics
          </code>
        </div>
      </div>
    </div>
  );
};

// Quote Card Component for Export
const QuoteCard = React.forwardRef(({ result, customerName, selectedTariff, language }, ref) => {
  const t = translations[language];
  const today = new Date().toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  // Calculate nationalization cost (total - merchandise value)
  const merchandiseValue = result.import_costs.commercial_invoice_value.amount_mxn + result.import_costs.international_freight.amount_mxn;
  const nationalizationCost = result.total_landed_cost - merchandiseValue;

  return (
    <div ref={ref} className="quote-card-export">
      <div className="quote-header">
        <img 
          src={`${window.location.origin}/dma-logo.png`} 
          alt="DMA International" 
          className="quote-logo"
          crossOrigin="anonymous"
        />
        <div className="quote-header-info">
          <h2>{t.quoteTitle}</h2>
          <p className="quote-date">{t.quoteDate}: {today}</p>
        </div>
      </div>

      <div className="quote-info-row">
        {customerName && (
          <div className="quote-client">
            <strong>{t.client}:</strong> {customerName}
          </div>
        )}
        {selectedTariff && (
          <div className="quote-tariff-inline">
            <strong>{t.fraction}:</strong> {selectedTariff.code}
          </div>
        )}
      </div>

      <div className="quote-main-total">
        <div className="quote-main-amount">
          <span className="quote-main-label">{t.nationalizationCost}</span>
          <span className="quote-main-value">{formatCurrency(nationalizationCost)} MXN</span>
        </div>
        <div className="quote-reference">
          <span>{t.merchandiseValue} ${(merchandiseValue / result.exchange_rate).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})} USD</span>
        </div>
      </div>

      <table className="quote-table">
        <thead>
          <tr>
            <th>{t.concept}</th>
            <th>{t.amountMXN}</th>
          </tr>
        </thead>
        <tbody>
          <tr className="quote-table-section">
            <td colSpan="2"><strong>{t.customsTaxes}</strong></td>
          </tr>
          <tr>
            <td>{t.igiLabel}</td>
            <td>{formatCurrency(result.import_costs.igi.amount_mxn)}</td>
          </tr>
          <tr>
            <td>{t.dtaLabel}</td>
            <td>{formatCurrency(result.import_costs.dta.amount_mxn)}</td>
          </tr>
          <tr>
            <td>{t.ivaLabel}</td>
            <td>{formatCurrency(result.import_costs.vat.amount_mxn)}</td>
          </tr>
          <tr className="quote-table-subtotal">
            <td><strong>{t.subtotalTaxes}</strong></td>
            <td><strong>{formatCurrency(result.import_costs.igi.amount_mxn + result.import_costs.dta.amount_mxn + result.import_costs.vat.amount_mxn)}</strong></td>
          </tr>

          <tr className="quote-table-section">
            <td colSpan="2"><strong>{t.dispatchLogistics}</strong></td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Servicio Integral de Importación 24/7' : 'Comprehensive Import Service 24/7'}</td>
            <td>{formatCurrency(result.additional_costs.import_service?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Maniobras Portuarias Estimadas' : 'Estimated Port Handling'}</td>
            <td>{formatCurrency(result.additional_costs.port_handling?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Manifestación de Valor Electrónica' : 'Electronic Value Manifest'}</td>
            <td>{formatCurrency(result.additional_costs.electronic_manifest?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Posible Almacenaje y Maniobras' : 'Possible Storage and Handling'}</td>
            <td>{formatCurrency(result.additional_costs.possible_storage?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Honorarios de Agente Aduanal' : 'Customs Broker Fee'}</td>
            <td>{formatCurrency(result.additional_costs.customs_broker_fee?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Flete Terrestre Directo' : 'Direct Inland Transportation'}</td>
            <td>{formatCurrency(result.additional_costs.inland_freight?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Lavado de Contenedor' : 'Container Washing'}</td>
            <td>{formatCurrency(result.additional_costs.container_washing?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Seguro de Carga Terrestre' : 'Inland Cargo Insurance'}</td>
            <td>{formatCurrency(result.additional_costs.cargo_insurance?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Cargos Locales de Naviera' : 'Shipping Line Local Charges'}</td>
            <td>{formatCurrency(result.additional_costs.shipping_line_charges?.amount_mxn || 0)}</td>
          </tr>
          <tr>
            <td>{language === 'es' ? 'Servicios DMA International' : 'DMA International Services'}</td>
            <td>{formatCurrency(result.additional_costs.dma_fee?.amount_mxn || 0)}</td>
          </tr>
          <tr className="quote-table-subtotal">
            <td><strong>{t.subtotalExpenses}</strong></td>
            <td><strong>{formatCurrency(result.additional_costs.subtotal)}</strong></td>
          </tr>

          <tr className="quote-table-total">
            <td><strong>{t.totalNationalization}</strong></td>
            <td><strong>{formatCurrency(nationalizationCost)}</strong></td>
          </tr>
        </tbody>
      </table>

      <div className="quote-exchange-info">
        <span>{t.appliedExchangeRate} <strong>${result.exchange_rate.toFixed(4)} MXN/USD</strong></span>
        <span>{t.units}: <strong>{result.units}</strong></span>
      </div>

      <div className="quote-disclaimer">
        <AlertTriangle size={16} />
        <p>
          <strong>{t.referentialQuote}</strong> - {t.quoteDisclaimer}
        </p>
      </div>

      <div className="quote-footer">
        <img 
          src={`${window.location.origin}/dma-logo.png`} 
          alt="DMA International" 
          className="quote-footer-logo"
          crossOrigin="anonymous"
        />
        <p>{t.dmaSlogan}</p>
        <p className="quote-contact">info@dma.international | www.dma.international</p>
      </div>
    </div>
  );
});

// Main App Component
function App() {
  const [activeTab, setActiveTab] = useState("calculator");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [categories, setCategories] = useState([
    { id: "general", name: "General / Other", name_es: "General / Otros" }
  ]);
  const [expandedSections, setExpandedSections] = useState({
    import: true,
    additional: true
  });

  // Language State
  const [language, setLanguage] = useState("es");

  // Customer Info State
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: ""
  });

  // Banxico Exchange Rate State
  const [banxicoRate, setBanxicoRate] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(17.5);

  // Leads State
  const [leads, setLeads] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  
  // Selected tariff for quote
  const [selectedTariff, setSelectedTariffState] = useState(null);
  
  // Quote ref for export
  const quoteRef = useRef(null);

  const [formData, setFormData] = useState({
    import_costs: {
      commercial_invoice_value: "",
      international_freight: "",
      igi_rate: "",
      ige_rate: "",
      dta_rate: 0.008,
      iva_rate: 0.16,
      exchange_rate: 17.5,
      units: "",
      product_category: "electronics"
    },
    additional_costs: {
      import_service: "",
      port_handling: "",
      electronic_manifest: "",
      possible_storage: "",
      customs_broker_fee: "",
      inland_freight: "",
      container_washing: "",
      cargo_insurance: "",
      shipping_line_charges: "",
      dma_fee: ""
    },
    target_margin: 0.25
  });

  // Sync exchangeRate with formData
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      import_costs: {
        ...prev.import_costs,
        exchange_rate: exchangeRate
      }
    }));
  }, [exchangeRate]);

  useEffect(() => {
    fetchCategories();
    fetchHistory();
    fetchBanxicoRate();
    fetchLeads();
    
    // Remove any watermark elements
    const removeWatermark = () => {
      const elements = document.querySelectorAll('[class*="emergent"], [data-testid*="emergent"], a[href*="emergent"]');
      elements.forEach(el => el.remove());
      
      // Also check for fixed positioned elements at bottom
      document.querySelectorAll('body > div, body > a').forEach(el => {
        const style = window.getComputedStyle(el);
        if (style.position === 'fixed' && parseInt(style.bottom) < 100) {
          el.style.display = 'none';
        }
      });
    };
    
    removeWatermark();
    const interval = setInterval(removeWatermark, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchBanxicoRate = async () => {
    try {
      const response = await axios.get(`${API}/banxico/exchange-rate`);
      setBanxicoRate(response.data);
      if (response.data.success && response.data.rate) {
        setExchangeRate(response.data.rate);
      }
    } catch (error) {
      console.error("Error fetching Banxico rate:", error);
    }
  };

  const fetchLeads = async () => {
    setLeadsLoading(true);
    try {
      const response = await axios.get(`${API}/leads`);
      setLeads(response.data);
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/product-categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/calculations/history`);
      setHistory(response.data);
    } catch (error) {
      console.error("Error fetching history:", error);
    }
  };

  const handleCalculate = async () => {
    setLoading(true);
    try {
      // Convert empty strings to 0 for calculation
      const cleanFormData = {
        import_costs: {
          commercial_invoice_value: formData.import_costs.commercial_invoice_value || 0,
          international_freight: formData.import_costs.international_freight || 0,
          igi_rate: formData.import_costs.igi_rate || 0,
          ige_rate: formData.import_costs.ige_rate || 0,
          dta_rate: formData.import_costs.dta_rate || 0.008,
          iva_rate: formData.import_costs.iva_rate || 0.16,
          exchange_rate: formData.import_costs.exchange_rate || 17.5,
          units: formData.import_costs.units || 1,
          product_category: formData.import_costs.product_category || "electronics"
        },
        additional_costs: {
          import_service: formData.additional_costs.import_service || 0,
          port_handling: formData.additional_costs.port_handling || 0,
          electronic_manifest: formData.additional_costs.electronic_manifest || 0,
          possible_storage: formData.additional_costs.possible_storage || 0,
          customs_broker_fee: formData.additional_costs.customs_broker_fee || 0,
          inland_freight: formData.additional_costs.inland_freight || 0,
          container_washing: formData.additional_costs.container_washing || 0,
          cargo_insurance: formData.additional_costs.cargo_insurance || 0,
          shipping_line_charges: formData.additional_costs.shipping_line_charges || 0,
          dma_fee: formData.additional_costs.dma_fee || 0
        },
        target_margin: formData.target_margin || 0.25
      };
      
      const response = await axios.post(`${API}/calculate`, cleanFormData);
      setResult(response.data);
      setActiveTab("results");
    } catch (error) {
      console.error("Calculation error:", error);
      alert("Error calculating costs. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculateWithMargin = async (newMargin) => {
    if (!result) return;
    
    setLoading(true);
    try {
      const updatedFormData = { ...formData, target_margin: newMargin };
      const response = await axios.post(`${API}/calculate`, updatedFormData);
      setResult(response.data);
      setFormData(updatedFormData);
    } catch (error) {
      console.error("Recalculation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCalculation = async () => {
    if (!result) return;
    const t = translations[language];
    const name = prompt(t.saveCalcPrompt);
    if (!name) return;

    try {
      await axios.post(`${API}/calculations/save?name=${encodeURIComponent(name)}`, result);
      fetchHistory();
      alert(t.calcSaved);
    } catch (error) {
      console.error("Error saving calculation:", error);
    }
  };

  const handleLoadCalculation = (item) => {
    setResult(item.result);
    setActiveTab("results");
  };

  const handleDeleteCalculation = async (id) => {
    const t = translations[language];
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await axios.delete(`${API}/calculations/${id}`);
      fetchHistory();
    } catch (error) {
      console.error("Error deleting calculation:", error);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Download quote as image
  const handleDownloadQuote = async () => {
    if (!quoteRef.current || !result) return;
    const t = translations[language];
    
    try {
      const dataUrl = await toPng(quoteRef.current, {
        quality: 0.95,
        backgroundColor: '#ffffff',
        pixelRatio: 2
      });
      
      const link = document.createElement('a');
      link.download = `${language === 'es' ? 'cotizacion' : 'quote'}-dma-${customerInfo.name || (language === 'es' ? 'cliente' : 'client')}-${new Date().toISOString().split('T')[0]}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error generating quote image:', error);
      alert(t.quoteError);
    }
  };

  const t = translations[language];

  return (
    <div className="app" data-testid="app-container">
      <header className="app-header">
        <div className="logo">
          <img src="/dma-logo.png" alt="DMA International" className="header-logo" />
        </div>
        <div className="header-title">
          <h1>{t.title}</h1>
        </div>
        <button 
          className="language-toggle-btn" 
          onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
          data-testid="language-toggle"
        >
          <Globe size={18} />
          {language === 'es' ? 'EN' : 'ES'}
        </button>
      </header>

      <TabNavigation 
        activeTab={activeTab} 
        setActiveTab={setActiveTab} 
        calculationComplete={!!result}
        language={language}
      />

      <main className="app-main">
        {activeTab === "calculator" && (
          <>
            {/* Customer Info Section */}
            <CustomerInfoForm 
              customerInfo={customerInfo}
              setCustomerInfo={setCustomerInfo}
              language={language}
            />

            {/* Calculator Form */}
            <CalculatorForm
              formData={formData}
              setFormData={setFormData}
              onCalculate={handleCalculate}
              loading={loading}
              categories={categories}
              banxicoRate={banxicoRate}
              exchangeRate={exchangeRate}
              setExchangeRate={setExchangeRate}
              language={language}
            />
          </>
        )}

        {activeTab === "results" && result && (
          <div className="results-tab" data-testid="results-tab">
            <ResultsSummary result={result} language={language} />
            
            <div className="cost-breakdowns">
              <CostBreakdown
                title={t.importCosts}
                costs={result.import_costs}
                subtotal={result.import_costs.subtotal}
                expanded={expandedSections.import}
                onToggle={() => toggleSection("import")}
                icon={Globe}
                language={language}
              />
              <CostBreakdown
                title={t.additionalCosts}
                costs={result.additional_costs}
                subtotal={result.additional_costs.subtotal}
                expanded={expandedSections.additional}
                onToggle={() => toggleSection("additional")}
                icon={Truck}
                language={language}
              />
            </div>

            <div className="results-grid">
              <NOMRequirements 
                requirements={result.nom_requirements} 
                category={result.product_category}
                language={language}
              />
            </div>

            <div className="results-actions">
              <button onClick={handleDownloadQuote} className="download-quote-btn" data-testid="download-quote-btn">
                <Download size={18} />
                {t.downloadQuote}
              </button>
              <button onClick={handleSaveCalculation} className="save-btn" data-testid="save-calculation-btn">
                {t.saveCalc}
              </button>
              <button onClick={() => setActiveTab("calculator")} className="edit-btn" data-testid="edit-calculation-btn">
                {t.edit}
              </button>
            </div>

            {/* Hidden Quote Card for Export */}
            <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
              <QuoteCard 
                ref={quoteRef}
                result={result}
                customerName={customerInfo.name}
                selectedTariff={selectedTariff}
                language={language}
              />
            </div>
          </div>
        )}

        {activeTab === "scenarios" && result && (
          <div className="scenarios-tab" data-testid="scenarios-tab">
            <h2>{t.scenarioComparison}</h2>
            <p>{t.scenarioDesc}</p>
            <div className="scenario-placeholder">
              <GitBranch size={48} />
              <p>{t.scenarioSoon}</p>
              <p>{t.scenarioSoonDesc}</p>
            </div>
          </div>
        )}

        {activeTab === "history" && (
          <div className="history-tab" data-testid="history-tab">
            <h2>{t.calcHistory}</h2>
            <HistoryTab 
              history={history}
              onLoad={handleLoadCalculation}
              onDelete={handleDeleteCalculation}
              language={language}
            />
          </div>
        )}

        {activeTab === "leads" && (
          <LeadsDashboard 
            leads={leads}
            onRefresh={fetchLeads}
            loading={leadsLoading}
            language={language}
          />
        )}

      </main>

      <footer className="app-footer">
        <div className="footer-disclaimer">
          <AlertTriangle size={16} />
          <p>
            <strong>{t.importantNotice}</strong> {t.footerDisclaimer}
          </p>
        </div>
        <div className="footer-brand">
          <img src="/dma-logo.png" alt="DMA International" className="footer-logo" />
          <p>© {new Date().getFullYear()} DMA International - {t.allRightsReserved}</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
